import { BundleProps, ProductData } from 'utils/types';
import { StyledSpinXBundle } from './styles';
import {
  EBadgeType,
  EBundlesInternalViewModel,
  EGradientDirection,
  EProductPriority,
  ESaleDiscountDisplayType,
} from 'utils/enums';
import { useEffect, useState } from 'react';
import { AutoTextSize } from 'utils/autoTextSizeUtils';
import { BundlePrice } from 'components/BundlePrice';
import { BadgeData, IEmblemBadge } from 'components/Badge/types';
import { useGetBadge } from 'utils/useGetBadge';
import { SpinXSaleBadge } from 'components/Badge/Sale/SpinXSaleBadge/SpinXSaleBadge';
import tooltipIcon from 'assets/SpinX/spin-x-tooltip.png';
import { SpecialOfferProps } from 'templates/SpecialOffer/types';

interface ISpinXTemplateProps {
  prices: BundleProps['prices'];
  productList: BundleProps['productList'];
  backgroundSrc: BundleProps['backgroundSrc'];
  backgroundColor: BundleProps['backgroundColor'];
  emblemBadges?: BundleProps['emblemBadges'];
  selectOffer?: SpecialOfferProps['selectOffer'];
  dataTestId?: string;
  isFree?: boolean;
  timer?: string;
  badges?: BadgeData[];
  salePercentage?: number;
  salePercentageDisplayType?: ESaleDiscountDisplayType;
  isSpecialOffer?: boolean;
  inMobileMock?: boolean;
}

export const SpinXTemplate: React.FC<ISpinXTemplateProps> = ({
  prices,
  productList,
  backgroundSrc,
  backgroundColor = {
    colorOne: '#406cf9',
    colorTwo: '#6449ff',
    gradientDirection: EGradientDirection.TopToBottom,
  },
  badges = [],
  emblemBadges = [],
  salePercentage = 0,
  salePercentageDisplayType,
  isSpecialOffer = false,
  inMobileMock = false,
}) => {
  const [mainProduct, setMainProduct] = useState<any>(null);
  const [subProducts, setSubProducts] = useState<ProductData[]>([]);
  const [emblemBadgeOld, setEmblemBadgeOld] = useState<any>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const offerButtonAsset =
    'https://media-dev.appcharge.com/media/659bce96140252be98df7c4b/1754156871958__8cd95daa-3184-4d76-ae1e-6faeadb7a891';

  const emblemBadgeNew = useGetBadge<IEmblemBadge>(emblemBadges);

  useEffect(() => {
    if (Array.isArray(badges) && badges.length > 0) {
      const foundEmblem = badges?.find((b: BadgeData) => {
        return (
          b.badge?.type === EBadgeType.EMBLEM || b.type === EBadgeType.EMBLEM
        );
      });
      setEmblemBadgeOld(foundEmblem?.badge?.emblem || foundEmblem?.emblem);
    } else {
      setEmblemBadgeOld(null);
    }
  }, [badges]);

  const emblemBadge = emblemBadgeNew ?? emblemBadgeOld;

  useEffect(() => {
    let main = productList.find(
      (product) => product.priority === EProductPriority.MAIN,
    );
    const sub = productList.filter(
      (product) => product.priority === EProductPriority.SUB,
    );

    setMainProduct(main);
    setSubProducts(sub);
  }, [productList]);

  return (
    <StyledSpinXBundle
      backgroundSrc={backgroundSrc}
      backgroundColor={backgroundColor}
      inMobileMock={inMobileMock}
    >
      <div className="container">
        {salePercentage > 0 && (
          <SpinXSaleBadge
            salePercentage={salePercentage}
            salePercentageDisplayType={salePercentageDisplayType}
          />
        )}
        {emblemBadge && (
          <div className="emblem">
            <img
              className="emblem-badge"
              alt="emblem-badge"
              src={emblemBadge.imageUrl}
            />
          </div>
        )}
        <div className="products">
          <div className="products-main">
            {mainProduct && (
              <img className="image" src={mainProduct.images.product} />
            )}
          </div>
          <div className="products-info">
            {mainProduct && mainProduct.quantityDisplay && (
              <div className="quantity-container">
                {mainProduct.strokedSaleQuantity && (
                  <AutoTextSize
                    maxFontSizePx={16}
                    minFontSizePx={6}
                    mode="oneline"
                    className="quantity-container__stroked"
                  >
                    WAS<span>{mainProduct.strokedSaleQuantity}</span>
                  </AutoTextSize>
                )}
                <AutoTextSize
                  maxFontSizePx={32}
                  minFontSizePx={6}
                  mode="oneline"
                  className="quantity-container__quantity"
                >
                  {mainProduct.quantityDisplay}
                </AutoTextSize>
              </div>
            )}
            {subProducts.length > 0 && (
              <div className="sub-products">
                {subProducts
                  .slice(0, 3)
                  .map(
                    ({ textFontColorHex, images, quantityDisplay }, index) => (
                      <div className="sub-products__container">
                        <div
                          style={{ color: textFontColorHex }}
                          className="sub-products__container-item"
                          key={index}
                        >
                          <img className="image" src={images.product} />
                          <AutoTextSize
                            className="quantity"
                            maxFontSizePx={16}
                            minFontSizePx={4}
                            mode="oneline"
                          >
                            {quantityDisplay}
                          </AutoTextSize>
                        </div>
                      </div>
                    ),
                  )}
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsTooltipOpen(!isTooltipOpen);
                  }}
                  className="sub-products__tooltip"
                >
                  <img className="icon" src={tooltipIcon} alt="tooltip" />
                </div>
                {isTooltipOpen && (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsTooltipOpen(false);
                    }}
                    className="tooltip-content"
                  >
                    Same purchase benefits as equivalent price tier in-game
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <BundlePrice
          prices={prices}
          type={EBundlesInternalViewModel.SPIN_X}
          offerButtonAsset={offerButtonAsset}
          isTextAutoResized
        />
      </div>
    </StyledSpinXBundle>
  );
};
